import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport } from 'ai';
import ChatColumn from '../Components/ChatColumn/ChatColumn';
import DataColumn from '../Components/DataColumn/DataColumn';
import InputMessage from '../Components/InputMessage/InputMessage';
import TemplateModal from '../Components/TemplateModal/TemplateModal';
import {
  useChatDataById,
  useDeleteMessage,
  useInitialMessages,
} from '../utils/queries';
import {
  StyledContainer,
  StyledGridItem,
  StyledPaper,
  ChatWrap,
  RightPanel,
  PanelInner,
} from './styles';

const PANEL_WIDTH = 'clamp(320px, 50vw, 800px)';

export default function ChatContainer({ apiHost: propsApiHost, courseId }) {
  const apiHost = propsApiHost || import.meta.env.VITE_APP_GRADEBOOK_API_URL;
  const inputRef = useRef(null);
  const [chatId, setChatId] = useState(uuidv4());
  const [modalOpen, setModalOpen] = useState(false);
  const [input, setInput] = useState('');
  const [selectedMessageId, setSelectedMessageId] = useState(null);
  const lastDataMessageIdRef = useRef(null);
  const [preferredToolName, setPreferredToolName] = useState(null);
  const [preferredChatDataId, setPreferredChatDataId] = useState(null);
  const [panelOpen, setPanelOpen] = useState(true);
  const didInitChatIdRef = useRef(false);

  const { data: initialMessagesData } = useInitialMessages({
    apiHost,
    courseId,
  });

  const deleteMutation = useDeleteMessage({ apiHost });
  const initialMessages = useMemo(
    () => initialMessagesData?.messages || [],
    [initialMessagesData],
  );

  const { messages, status, setMessages, sendMessage, error, regenerate } =
    useChat({
      id: chatId,
      transport: new DefaultChatTransport({
        api: `${apiHost}/api/v1/sections-chat/${courseId}/chat`,
        credentials: 'include',
        body: {
          courseId,
        },
      }),
      throttle: 50,
    });

  useEffect(() => {
    if (initialMessages.length > 0 && messages.length === 0) {
      setMessages(initialMessages);
    }
  }, [initialMessages, messages.length, setMessages]);

  // Auto-focus side panel on the newest message that streams data
  useEffect(() => {
    if (!Array.isArray(messages) || messages.length === 0) return;
    let lastWithDataId = null;
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (Array.isArray(m.parts)) {
        const hasData = m.parts.some(
          (p) => typeof p.type === 'string' && p.type.startsWith('data-'),
        );
        if (hasData) {
          lastWithDataId = m.id;
          break;
        }
      }
    }
    if (lastWithDataId && lastWithDataId !== lastDataMessageIdRef.current) {
      setSelectedMessageId(lastWithDataId);
      setPreferredToolName(null);
      setPreferredChatDataId(null);
      setPanelOpen(true);
    }
    lastDataMessageIdRef.current = lastWithDataId;
  }, [messages]);

  useEffect(() => {
    if (!didInitChatIdRef.current) {
      didInitChatIdRef.current = true;
      return;
    }
    setMessages([]);
  }, [chatId, setMessages]);

  const toggleModal = useCallback(() => {
    setModalOpen((prev) => !prev);
  }, []);

  const sendUserMessage = useCallback(
    (text) => {
      const messageId = uuidv4();
      sendMessage({
        id: messageId,
        role: 'user',
        parts: [{ type: 'text', text }],
        metadata: {
          createdAt: Date.now(),
        },
      });
    },
    [sendMessage],
  );

  const handleResend = useCallback(
    (message) => {
      const text = message.parts.find((part) => part.type === 'text')?.text;
      if (text) {
        sendUserMessage(text);
      }
    },
    [sendUserMessage],
  );

  const handleCopy = useCallback((parts) => {
    const text = parts.find((part) => part.type === 'text')?.text;
    setInput(text);
    inputRef.current?.focus();
  }, []);

  const handleDelete = useCallback(() => {
    deleteMutation.mutate(
      { courseId },
      {
        onSuccess: () => {
          setMessages([]);
          setChatId(uuidv4());
        },
      },
    );
  }, [courseId, deleteMutation, setMessages]);

  const handleTemplateSelected = useCallback(
    (template) => {
      sendUserMessage(template);
    },
    [sendUserMessage],
  );

  const handleInputChange = useCallback((e) => {
    setInput(e.target.value);
  }, []);

  const handleSubmit = useCallback(() => {
    if (input.trim()) {
      sendUserMessage(input);
      setInput('');
    }
  }, [input, sendUserMessage]);

  const selectedMessage = useMemo(() => {
    if (!messages || messages.length === 0) return null;
    return (
      messages.find((m) => m.id === selectedMessageId) ||
      messages[messages.length - 1]
    );
  }, [messages, selectedMessageId]);

  const handleSelect = useCallback((messageId, toolName, chatDataId) => {
    setSelectedMessageId(messageId);
    setPreferredToolName(toolName || null);
    setPreferredChatDataId(chatDataId || null);
    setPanelOpen(true);
  }, []);

  const handleClosePanel = useCallback(() => {
    setPanelOpen(false);
    setSelectedMessageId(null);
    setPreferredToolName(null);
    setPreferredChatDataId(null);
  }, []);

  const getFromParts = useCallback((parts) => {
    const dataParts = parts.filter(
      (part) => typeof part.type === 'string' && part.type.startsWith('data-'),
    );
    if (dataParts.length === 0) return { toolName: null, toolData: null };
    const latestDataPart = dataParts[dataParts.length - 1];
    const payload = latestDataPart.data;
    const toolEntry = Object.entries(payload || {})?.[0] || [];
    return { toolName: toolEntry[0], toolData: toolEntry[1] };
  }, []);

  const getPreferredFromParts = useCallback(
    (parts) => {
      if (preferredChatDataId) {
        const matchById = parts.find((p) => p.id === preferredChatDataId);
        if (matchById) {
          const payload = matchById.data;
          const toolEntry = Object.entries(payload || {})?.[0] || [];
          return { toolName: toolEntry[0], toolData: toolEntry[1] };
        }
      }
      if (preferredToolName) {
        const dataKey = preferredToolName;
        const matchByTool = parts.find(
          (p) =>
            typeof p.type === 'string' &&
            p.type.startsWith('data-') &&
            p.data &&
            dataKey &&
            p.data[dataKey],
        );
        if (matchByTool) {
          return {
            toolName: preferredToolName,
            toolData: matchByTool.data[dataKey],
          };
        }
      }
      return { toolName: null, toolData: null };
    },
    [preferredChatDataId, preferredToolName],
  );

  // Determine if we need to fetch data by ID (when we don't have it in memory)
  const parts = useMemo(() => selectedMessage?.parts || [], [selectedMessage]);
  const hasDataInMemory = useMemo(() => {
    if (parts.length === 0) return false;
    if (preferredToolName || preferredChatDataId) {
      const preferred = getPreferredFromParts(parts);
      return !!(preferred.toolName && preferred.toolData);
    }
    const fallback = getFromParts(parts);
    return !!(fallback.toolName && fallback.toolData);
  }, [
    parts,
    preferredToolName,
    preferredChatDataId,
    getPreferredFromParts,
    getFromParts,
  ]);

  const shouldFetchById = !hasDataInMemory && !!preferredChatDataId;

  const { data: chatDataByIdResponse } = useChatDataById({
    courseId,
    chatDataId: preferredChatDataId,
    apiHost,
    enabled: shouldFetchById,
  });

  const { toolName, toolData } = useMemo(() => {
    // 1) Prefer persistent data parts on the selected message (when present in memory)
    if (parts.length > 0) {
      if (preferredToolName || preferredChatDataId) {
        const preferred = getPreferredFromParts(parts);
        if (preferred.toolName && preferred.toolData) return preferred;
      }
      const fallbackParts = getFromParts(parts);
      if (fallbackParts.toolName && fallbackParts.toolData)
        return fallbackParts;
    }

    // 2) Fallback: fetch from endpoint when preferredChatDataId is set
    const chatMessageData = chatDataByIdResponse?.chatMessageData;
    if (preferredChatDataId && chatMessageData) {
      const payload = chatMessageData?.message_data;

      if (preferredToolName && payload?.[preferredToolName]) {
        return {
          toolName: preferredToolName,
          toolData: payload[preferredToolName],
        };
      }

      if (payload) {
        const toolEntry = Object.entries(payload)?.[0] || [];
        return { toolName: toolEntry[0], toolData: toolEntry[1] };
      }
    }

    return { toolName: null, toolData: null };
  }, [
    parts,
    preferredToolName,
    preferredChatDataId,
    getPreferredFromParts,
    getFromParts,
    chatDataByIdResponse?.chatMessageData,
  ]);

  const hasData = useMemo(
    () => panelOpen && !!toolName && !!toolData,
    [panelOpen, toolName, toolData],
  );

  return (
    <StyledContainer container style={{ '--panelWidth': PANEL_WIDTH }}>
      <StyledGridItem item xs={12}>
        <ChatWrap open={hasData}>
          <StyledPaper>
            <ChatColumn
              messages={messages}
              onSelect={handleSelect}
              onCopy={handleCopy}
              onResend={handleResend}
              status={status}
              error={error}
              regenerate={regenerate}
            />
            <InputMessage
              value={input}
              inputRef={inputRef}
              onSubmit={handleSubmit}
              onChange={handleInputChange}
              onDelete={handleDelete}
              toggleModal={toggleModal}
              status={status}
            />
            <TemplateModal
              open={modalOpen}
              onClose={toggleModal}
              onTemplateSelected={handleTemplateSelected}
              courseId={courseId}
              apiHost={apiHost}
            />
          </StyledPaper>
        </ChatWrap>
      </StyledGridItem>

      <RightPanel open={hasData} role="complementary" aria-hidden={!hasData}>
        <PanelInner open={hasData}>
          {hasData && (
            <StyledPaper>
              <DataColumn
                toolName={toolName}
                toolData={toolData}
                onClose={handleClosePanel}
              />
            </StyledPaper>
          )}
        </PanelInner>
      </RightPanel>
    </StyledContainer>
  );
}

ChatContainer.propTypes = {
  apiHost: PropTypes.string,
  courseId: PropTypes.string,
};
